import { css, cx } from '@emotion/css';
import { useEffect, useMemo, type HTMLAttributes } from 'react';
import { Wallet, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/use-auth';
import { useGetUserBalance } from '../../hooks/checkout-api.hooks';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const balanceInfo = css({
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--g-50)',
  borderRadius: 'var(--spacing-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
});

const balanceRow = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 'var(--spacing-sm) 0',
});

const balanceLabel = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  fontWeight: 500,
});

const balanceValue = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-800)',
  fontWeight: 600,
});

const divider = css({
  height: '1px',
  backgroundColor: 'var(--g-200)',
  margin: 'var(--spacing-xs) 0',
});

const messageBox = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-md)',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
});

const insufficientBox = css({
  backgroundColor: 'rgba(244, 67, 54, 0.1)',
  borderLeft: '4px solid #f44336',
  color: '#d32f2f',
});

const sufficientBox = css({
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  borderLeft: '4px solid #4CAF50',
  color: '#2E7D32',
});

const loadingBox = css({
  display: 'flex',
  justifyContent: 'center',
  padding: 'var(--spacing-xl)',
  color: 'var(--g-600)',
  fontStyle: 'italic',
});

export interface BalanceData {
  paymentMethodType: 'balance';
  balance: number;
}

export interface PaymentMethodBalanceProps extends HTMLAttributes<HTMLDivElement> {
  totalValue?: number;
  onDataChange?: (data: BalanceData, isValid: boolean) => void;
}

export function PaymentMethodBalance({
  totalValue = 0,
  onDataChange,
  className,
  style,
  ...props
}: PaymentMethodBalanceProps) {
  const { user } = useAuth();
  const { data, isLoading, isError } = useGetUserBalance(!!user);
  const balance = useMemo(() => data?.balance ?? 0, [data]);

  useEffect(() => {
    const hasEnoughBalance = balance >= totalValue;
    onDataChange?.(
      { paymentMethodType: 'balance', balance },
      hasEnoughBalance
    );
  }, [balance, totalValue, onDataChange]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const hasEnoughBalance = balance >= totalValue;

  if (isLoading) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        <div className={loadingBox}>Carregando informações de saldo...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        <div className={cx(messageBox, insufficientBox)}>
          <AlertCircle size={20} style={{ minWidth: '20px', marginTop: '2px' }} />
          <span>Não foi possível carregar o seu saldo agora. Tente novamente em instantes.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <div className={balanceInfo}>
        <div className={balanceRow}>
          <span className={balanceLabel}>
            <Wallet size={20} style={{ display: 'inline', marginRight: '8px' }} />
            Seu saldo:
          </span>
          <span className={balanceValue}>{formatCurrency(balance)}</span>
        </div>

        <div className={divider} />

        <div className={balanceRow}>
          <span className={balanceLabel}>Valor da compra:</span>
          <span className={balanceValue}>{formatCurrency(totalValue)}</span>
        </div>
      </div>

      {!hasEnoughBalance && (
        <div className={cx(messageBox, insufficientBox)}>
          <AlertCircle size={20} style={{ minWidth: '20px', marginTop: '2px' }} />
          <span>
            Saldo insuficiente para completar esta compra. Por favor, escolha outro
            método de pagamento.
          </span>
        </div>
      )}

      {hasEnoughBalance && (
        <div className={cx(messageBox, sufficientBox)}>
          <CheckCircle size={20} style={{ minWidth: '20px', marginTop: '2px' }} />
          <span>
            Você tem saldo suficiente para completar esta compra. Clique em "Finalizar
            Compra" para confirmar.
          </span>
        </div>
      )}
    </div>
  );
}

export default PaymentMethodBalance;
